/**
 * Organizzazioni (ADR-0074 "modello Organizzazione") — the commercial anagrafica.
 *
 * The problem it solves: Control knew a paying company only as a tenant row (SaaS) OR a
 * license row (on-prem), so the same company was two unrelated records and "organizzazione" meant
 * two different things depending on which page you were on. Here a Organizzazione is the company,
 * and the row expands to show everything it holds — its cloud workspaces AND its on-prem
 * deployments — on one screen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Building2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { type OrganizationDuplicate, type PlatformOrganization, platformApi } from "@/lib/platform";

import { OrganizationDialog } from "./OrganizationDialog";

const ORGANIZATIONS_KEY = ["platform", "organizations"];

/** The badge tooltip names WHO and WHY ("stessa P.IVA di ACME Srl") — a bare warning icon
 * would send the operator hunting; the cure (move + archive) starts from knowing the
 * sibling. Detection only, never an automatic merge (ADR-0076 decision 9). */
const DUPLICATE_KIND_LABEL: Record<OrganizationDuplicate["kind"], string> = {
  vat: "stessa P.IVA di",
};

function duplicateHint(dups: OrganizationDuplicate[]): string {
  return dups.map((d) => `${DUPLICATE_KIND_LABEL[d.kind] ?? d.kind} ${d.name}`).join(" · ");
}

export function PlatformOrganizationsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState<PlatformOrganization | null | undefined>(undefined);
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: [...ORGANIZATIONS_KEY, { search }],
    queryFn: () => platformApi.listOrganizations({ search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ORGANIZATIONS_KEY });

  const archive = useMutation({
    mutationFn: (c: PlatformOrganization) =>
      platformApi.updateOrganization(c.id, {
        status: c.status === "archived" ? "active" : "archived",
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformApi.deleteOrganization(id),
    onSuccess: invalidate,
  });

  const rows = q.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Organizzazioni"
        description="L'anagrafica commerciale: un'organizzazione possiede i suoi workspace cloud e le sue installazioni on-prem."
        actions={
          <Button onClick={() => setEditing(null)}>
            <Plus className="mr-1.5 h-4 w-4" /> Nuova organizzazione
          </Button>
        }
      />
      <PageContent>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Cerca per nome, ragione sociale, P.IVA o email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {rows.length === 0 && !q.isLoading ? (
          <EmptyState
            icon={Building2}
            title="Nessuna organizzazione"
            description={
              search
                ? "Nessun risultato per questa ricerca."
                : "Le organizzazioni nascono da sole quando arriva una registrazione self-service o quando emetti una licenza on-prem. Puoi anche crearne una a mano."
            }
          />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Organizzazione</Th>
                <Th>P.IVA</Th>
                <Th>Email</Th>
                <Th className="text-right">Workspace</Th>
                <Th className="text-right">On-prem</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((c) => (
                <Tr
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => navigate(c.id)}
                  role="link"
                  aria-label={`Apri ${c.name}`}
                >
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      {c.status === "archived" && <Badge tone="neutral">Archiviata</Badge>}
                      {(c.duplicates ?? []).length > 0 && (
                        <Badge tone="warning" title={duplicateHint(c.duplicates!)}>
                          Possibile duplicato
                        </Badge>
                      )}
                    </div>
                    {c.legal_name && (
                      <div className="text-xs text-muted-foreground">{c.legal_name}</div>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">{c.vat_number || "—"}</Td>
                  <Td className="text-muted-foreground">{c.email || "—"}</Td>
                  <Td className="text-right tabular-nums">{c.tenant_count}</Td>
                  <Td className="text-right tabular-nums">{c.license_count}</Td>
                  <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Modifica"
                        onClick={() => setEditing(c)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={c.status === "archived" ? "Riattiva" : "Archivia"}
                        title="Archiviare non tocca workspace e licenze collegati"
                        onClick={() => archive.mutate(c)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Elimina"
                        title="Elimina solo la scheda commerciale: workspace e licenze restano attivi, senza organizzazione"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Eliminare la scheda di "${c.name}"? I suoi ${c.tenant_count} workspace e ${c.license_count} licenze restano attivi, ma senza organizzazione collegata.`,
                            )
                          )
                            remove.mutate(c.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </PageContent>

      {editing !== undefined && (
        <OrganizationDialog
          organization={editing}
          onClose={() => setEditing(undefined)}
          onSaved={invalidate}
        />
      )}
    </PageContainer>
  );
}
